import { Skeleton, Table, TableRow, TableHead, TableBody, TableCell, useTheme, Grid } from '@mui/material';
import {
	CerebroBreadcrumb,
	CerebroDrawer,
	CerebroHeading,
	CerebroIconButton,
	CerebroPageContainer,
	CerebroText,
	EstimatedDebtCapacityChart,
	OutputAnalysisTable,
} from '../shared/components';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useOutputAnalysisStructureQuery } from '../shared/hooks/Queries/useOutputAnalysisStructureQuery';
import {
	CreditOutputAnalysis,
	CreditOutputLineItem,
	OutputAnalysisItem,
	OutputAnalysisStatus,
	OutputAnalysisStructure,
} from '../shared/models';
import { EnumUtils } from '../shared/utils';
import { useAuth } from '../shared/context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/pro-duotone-svg-icons';
import { EmptyGuid } from '../shared/constants';
import { OutputAnalysisItemForm } from './OutputAnalysisItemForm';
import { OutputAnalysisHeaderForm } from './OutputAnalysisHeaderForm';
import { useDealQuery } from '../shared/hooks';

export const OutputAnalysis: React.FC = () => {
	const theme = useTheme();
	const [selectedOutputAnalysis, setSelectedOutputAnalysis] = React.useState<SelectedOutputAnalysisItem | null>(null);
	const [lineItem, setLineItem] = React.useState<CreditOutputLineItem | null>(null);
	const [header, setHeader] = React.useState<CreditOutputAnalysis | null>(null);

	const { guid } = useParams<'guid'>();
	const dealQuery = useDealQuery(guid);
	const analysisQuery = useOutputAnalysisStructureQuery(guid, {
		enabled: dealQuery.isSuccess,
		retry: false,
	});
	const { currentUser } = useAuth();
	const isEditMode =
		dealQuery.isSuccess &&
		analysisQuery.isSuccess &&
		dealQuery.data.isEditingCreditAnalysis &&
		currentUser.isEmployeeUser;

	const onViewAnalysisItem = (original: OutputAnalysisItem, item: CreditOutputLineItem | null) => {
		setSelectedOutputAnalysis({
			analysis: original,
			overrideItem: item,
		} as SelectedOutputAnalysisItem);
	};

	const onEditAnalysisHeader = (structure: OutputAnalysisStructure) => {
		if (structure.header != null) {
			setHeader(structure.header);
		} else {
			setHeader({
				id: 0,
				guid: EmptyGuid,
				debtCapacity: structure.debtCapacity,
				isBalanceSheetSectionVisible: true,
				isBusinessSectionVisible: true,
				isCashFlowSectionVisible: true,
				isProfitLossSectionVisible: true,
			} as CreditOutputAnalysis);
		}
	};

	const onEditAnalysisItem = (original: OutputAnalysisItem, item: CreditOutputLineItem | null) => {
		if (item != null) {
			setLineItem(item);
		} else {
			setLineItem({
				id: 0,
				guid: EmptyGuid,
				displayIndex: original.displayIndex,
				groupType: original.groupType,
				outputStatus: original.outputStatus,
				outputType: original.outputType,
				comments: original.comments,
				isVisibleToBorrower: true,
				summaryStatus: original.summaryStatus,
			} as CreditOutputLineItem);
		}
	};

	const onCloseDrawersAndDialogs = () => {
		setSelectedOutputAnalysis(null);
		setLineItem(null);
		setHeader(null);
	};

	return (
		<CerebroPageContainer>
			<CerebroBreadcrumb>Credit Analysis</CerebroBreadcrumb>

			<CerebroHeading component="h1" size="h1" sx={{ marginBottom: 3 }}>
				Analysis Output
			</CerebroHeading>
			<CerebroHeading component="h2" size="h2" sx={{ marginBottom: 3 }}>
				Estimated Debt Capacity
				{isEditMode && (
					<CerebroIconButton
						color="info"
						onClick={() => onEditAnalysisHeader(analysisQuery.data)}
						title="To edit the output details"
					>
						<FontAwesomeIcon icon={faEdit} size="xs" />
					</CerebroIconButton>
				)}
			</CerebroHeading>

			{analysisQuery.isSuccess ? (
				<>
					<EstimatedDebtCapacityChart
						remaining={analysisQuery.data.remainingDebt}
						refinanced={analysisQuery.data.refinancedDebt}
						incremental={analysisQuery.data.incrementalDebt}
						proforma={analysisQuery.data.proformaDebt}
						debtCapacity={analysisQuery.data.debtCapacity}
						header={analysisQuery.data.header}
					/>

					<OutputAnalysisTable
						key="ProfitLoss"
						group="ProfitLoss"
						outputItems={analysisQuery.data.outputItems.filter(output => output.groupType == 'ProfitLoss')}
						items={analysisQuery.data.items.filter(output => output.groupType == 'ProfitLoss')}
						isEditMode={isEditMode}
						isVisibleToBorrower={analysisQuery.data.header?.isProfitLossSectionVisible ?? true}
						onViewOutputAnalysis={onViewAnalysisItem}
						onEditAnalysisItem={onEditAnalysisItem}
					/>

					<OutputAnalysisTable
						key="CashFlows"
						group="CashFlows"
						outputItems={analysisQuery.data.outputItems.filter(output => output.groupType == 'CashFlows')}
						items={analysisQuery.data.items.filter(output => output.groupType == 'CashFlows')}
						isEditMode={isEditMode}
						isVisibleToBorrower={analysisQuery.data.header?.isCashFlowSectionVisible ?? true}
						onViewOutputAnalysis={onViewAnalysisItem}
						onEditAnalysisItem={onEditAnalysisItem}
					/>

					<OutputAnalysisTable
						key="BalanceSheet"
						group="BalanceSheet"
						outputItems={analysisQuery.data.outputItems.filter(
							output => output.groupType == 'BalanceSheet'
						)}
						items={analysisQuery.data.items.filter(output => output.groupType == 'BalanceSheet')}
						isEditMode={isEditMode}
						isVisibleToBorrower={analysisQuery.data.header?.isBalanceSheetSectionVisible ?? true}
						onViewOutputAnalysis={onViewAnalysisItem}
						onEditAnalysisItem={onEditAnalysisItem}
					/>

					<OutputAnalysisTable
						key="Business"
						group="Business"
						outputItems={analysisQuery.data.outputItems.filter(output => output.groupType == 'Business')}
						items={analysisQuery.data.items.filter(output => output.groupType == 'Business')}
						isEditMode={isEditMode}
						isVisibleToBorrower={analysisQuery.data.header?.isBusinessSectionVisible ?? true}
						onViewOutputAnalysis={onViewAnalysisItem}
						onEditAnalysisItem={onEditAnalysisItem}
					/>
				</>
			) : (
				<>
					<Skeleton height={300} />
					<Skeleton height={300} />
					<Skeleton height={300} />
					<Skeleton height={300} />
					<Skeleton height={300} />
				</>
			)}

			<CerebroDrawer open={selectedOutputAnalysis != null} onClose={() => setSelectedOutputAnalysis(null)}>
				<CerebroHeading sx={{ marginBottom: 3 }}>
					{EnumUtils.GetOutputAnalysisTypeDescription(selectedOutputAnalysis?.analysis.outputType)}
				</CerebroHeading>

				<Grid container spacing={3} sx={{ marginBottom: 3 }}>
					<Grid item xs={3}>
						<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
							Value:
						</CerebroText>
					</Grid>
					<Grid item xs={9}>
						<CerebroText>{selectedOutputAnalysis?.analysis.value}</CerebroText>
					</Grid>
					<Grid item xs={3}>
						<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
							Current Rating:
						</CerebroText>
					</Grid>
					<Grid item xs={9}>
						<CerebroText>
							{EnumUtils.GetOutputAnalysisStatusDescription(getOutputStatus(selectedOutputAnalysis))}
						</CerebroText>
					</Grid>
				</Grid>
				{selectedOutputAnalysis?.analysis.formulas && selectedOutputAnalysis.analysis.formulas.length > 0 && (
					<Grid container spacing={3} sx={{ marginBottom: 3 }}>
						{selectedOutputAnalysis.analysis.formulas.map((formula, index) => {
							return (
								<>
									<Grid key={`formulaName-${index}`} item xs={3}>
										<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
											{formula.name}:
										</CerebroText>
									</Grid>
									<Grid key={`formulaConditions-${index}`} item xs={9}>
										<CerebroText>{formula.conditions}</CerebroText>
									</Grid>
								</>
							);
						})}
					</Grid>
				)}

				{selectedOutputAnalysis?.analysis.ratings && selectedOutputAnalysis.analysis.ratings.length > 0 && (
					<Table sx={{ marginBottom: 3, border: `1px solid ${theme.palette.gray.border}` }}>
						<TableHead>
							<TableRow>
								<TableCell>
									<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
										Rating
									</CerebroText>
								</TableCell>
								<TableCell>
									<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
										Conditions
									</CerebroText>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{selectedOutputAnalysis.analysis.ratings.map(rating => {
								const conditions = rating.conditions.split('|');
								return (
									<TableRow
										key={rating.outputStatus}
										// sx={{
										// 	backgroundColor:
										// 		selectedOutputAnalysis?.outputStatus == rating.outputStatus
										// 			? theme.palette.primary.light
										// 			: null,
										// }}
									>
										<TableCell width={200}>
											<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
												{EnumUtils.GetOutputAnalysisStatusDescription(rating.outputStatus)}
											</CerebroText>
										</TableCell>
										<TableCell>
											{conditions.map((element, index) => {
												return (
													<CerebroText key={`${rating.outputStatus}-${index}`}>
														{element}
													</CerebroText>
												);
											})}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}

				{selectedOutputAnalysis?.analysis.variables && selectedOutputAnalysis.analysis.variables.length > 0 && (
					<Table sx={{ marginBottom: 3, border: '1px solid rgba(224, 224, 224, 1)' }}>
						<TableHead>
							<TableRow>
								<TableCell>
									<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
										Definition
									</CerebroText>
								</TableCell>
								<TableCell>
									<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
										Conditions
									</CerebroText>
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{selectedOutputAnalysis.analysis.variables.map(variable => {
								return (
									<TableRow key={variable.definition}>
										<TableCell>
											<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
												{variable.definition}
											</CerebroText>
										</TableCell>
										<TableCell>
											<CerebroText>{variable.value}</CerebroText>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}

				{getComments(selectedOutputAnalysis) && (
					<Grid container spacing={3} sx={{ marginBottom: 3 }}>
						<Grid item xs={12}>
							<CerebroText variant="small" sx={{ fontWeight: 'bold' }}>
								Comments
							</CerebroText>
						</Grid>
						<Grid item xs={12}>
							<CerebroText>{getComments(selectedOutputAnalysis)}</CerebroText>
						</Grid>
					</Grid>
				)}
			</CerebroDrawer>

			<OutputAnalysisHeaderForm header={header} close={onCloseDrawersAndDialogs} />
			<OutputAnalysisItemForm item={lineItem} close={onCloseDrawersAndDialogs} />
		</CerebroPageContainer>
	);
};

interface SelectedOutputAnalysisItem {
	analysis: OutputAnalysisItem;
	overrideItem: CreditOutputLineItem | null;
}

function getComments(item: SelectedOutputAnalysisItem | null | undefined) {
	if (!item) {
		return '';
	}

	return item.overrideItem ? item.overrideItem.comments : item.analysis.comments;
}

function getOutputStatus(item: SelectedOutputAnalysisItem | null | undefined) {
	if (!item) {
		return 'MissingData' as OutputAnalysisStatus;
	}

	return item.overrideItem ? item.overrideItem.outputStatus : item.analysis.outputStatus;
}
